import {
  ComponentPropsWithoutRef,
  ElementType,
  FormEvent,
  HTMLInputTypeAttribute,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { VariantProps, tv } from 'tailwind-variants';

import Text from '../base/Text';
import {
  UseAutoResizeTextareaOptions,
  useAutoResizeTextarea,
} from '../hooks/useAutoResizeTextarea';

export const input = tv({
  base: 'bg-gray-15 hover:bg-gray-30 dark:bg-gray-75 focus-visible:bg-gray-30 dark:hover:bg-gray-60 dark:focus-visible:bg-gray-60 placeholder:text-gray-45 outline-hidden w-full min-w-0 rounded-lg text-[16px]/[20px] transition-all sm:text-[14px]/[18px] dark:text-white',
  variants: {
    as: {
      input: 'h-10 px-4',
      textarea: 'h-[120px] resize-none p-4',
    },
  },
  defaultVariants: {
    as: 'input',
  },
});

type PolymorphicAsProp<E extends ElementType> = {
  as?: E;
};

type PolymorphicProps<E extends ElementType> = PropsWithChildren<
  ComponentPropsWithoutRef<E> & PolymorphicAsProp<E>
>;

export type InputProps<E extends ElementType = typeof defaultElement> =
  PolymorphicProps<E> & {
    name: string;
    label?: ReactNode;
    type?: HTMLInputTypeAttribute;
    placeholder?: string;
    className?: string;
    defaultValue?: string;
    as?: VariantProps<typeof input>['as'];
    autoResize?: boolean;
    autoResizeOptions?: UseAutoResizeTextareaOptions;
    autoComplete?: string;
    required?: boolean;
  };

const defaultElement = 'input';

export default function Input<E extends ElementType = typeof defaultElement>({
  name,
  label,
  type = 'text',
  placeholder,
  className,
  defaultValue,
  as,
  autoResize = false,
  autoResizeOptions,
  required,
  ...props
}: InputProps<E>) {
  const InputElement: 'input' | 'textarea' = as || 'input';
  const isTextarea = InputElement === 'textarea';

  // Use auto-resize hook only for textareas when autoResize is enabled
  const { textareaRef, handleInput } = useAutoResizeTextarea(
    isTextarea && autoResize ? autoResizeOptions : undefined,
  );

  // Merge the auto-resize input handler with any existing onInput prop
  const inputProps =
    isTextarea && autoResize
      ? {
          ...props,
          ref: textareaRef,
          onInput: (e: FormEvent<HTMLTextAreaElement>) => {
            handleInput(e);
            if (props.onInput) {
              props.onInput(e as FormEvent<HTMLTextAreaElement>);
            }
          },
        }
      : props;

  if (!label) {
    return (
      <InputElement
        type={type}
        name={name}
        placeholder={placeholder}
        className={input({ className, as })}
        defaultValue={defaultValue}
        {...inputProps}
        required={required}
        {...props}
      />
    );
  }

  return (
    <Text
      as="label"
      variant="studio/body/medium/sm"
      className={`text-gray-90 flex flex-col gap-2.5 dark:text-white ${className}`}
    >
      {label}
      <InputElement
        type={type}
        name={name}
        placeholder={placeholder}
        className={input({ as })}
        defaultValue={defaultValue}
        required={required}
        {...props}
      />
    </Text>
  );
}
